import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, Table, FormCheck } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { athleteService } from '../../services/firebaseService';

const SimpleImportAthletes = ({ tournamentId, onImportSuccess }) => {
  const [file, setFile] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [step, setStep] = useState('upload'); // 'upload', 'preview', 'importing', 'complete'

  // Gérer le changement de fichier
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setError(null);
  };

  // Gérer la lecture du fichier
  const handleReadFile = async () => {
    if (!file) {
      setError('Veuillez sélectionner un fichier.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Vérifier le type de fichier
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
        setError('Format de fichier non pris en charge. Veuillez utiliser un fichier Excel (.xlsx, .xls) ou CSV (.csv).');
        setLoading(false);
        return;
      }

      // Lire le fichier
      const data = await readExcelFile(file);

      if (!data || data.length === 0) {
        setError('Le fichier est vide ou ne contient pas de données valides.');
        setLoading(false);
        return;
      }

      // Valider et transformer les données
      const validatedAthletes = validateAndTransformData(data);

      if (validatedAthletes.length === 0) {
        setError('Aucune donnée valide n\'a été trouvée dans le fichier. Assurez-vous que le fichier suit le format requis.');
        setLoading(false);
        return;
      }

      // Mettre à jour l'état
      setAthletes(validatedAthletes);
      // Par défaut, tous les athlètes sont sélectionnés
      setSelectedAthletes(validatedAthletes.map((_, index) => index));
      setStep('preview');
    } catch (err) {
      console.error('Erreur lors de la lecture du fichier:', err);
      setError('Erreur lors de la lecture du fichier. Veuillez vérifier le format et réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Gérer la sélection/désélection de tous les athlètes
  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    
    if (checked) {
      // Sélectionner tous les athlètes
      setSelectedAthletes(athletes.map((_, index) => index));
    } else {
      // Désélectionner tous les athlètes
      setSelectedAthletes([]);
    }
  };

  // Gérer la sélection/désélection d'un athlète
  const handleSelectAthlete = (index) => {
    const isSelected = selectedAthletes.includes(index);
    
    if (isSelected) {
      // Désélectionner l'athlète
      setSelectedAthletes(selectedAthletes.filter(i => i !== index));
    } else {
      // Sélectionner l'athlète
      setSelectedAthletes([...selectedAthletes, index]);
    }
  };

  // Mettre à jour selectAll quand selectedAthletes change
  useEffect(() => {
    if (athletes.length > 0) {
      setSelectAll(selectedAthletes.length === athletes.length);
    }
  }, [selectedAthletes, athletes]);

  // Gérer l'importation des athlètes
  const handleImportAthletes = async () => {
    if (selectedAthletes.length === 0) {
      setError('Aucun athlète sélectionné pour l\'importation.');
      return;
    }

    try {
      setLoading(true);
      setStep('importing');
      setError(null);

      // Filtrer les athlètes sélectionnés
      const athletesToImport = selectedAthletes.map(index => athletes[index]);
      
      // Importer les athlètes sélectionnés
      const importedAthletes = await athleteService.importAthletes(athletesToImport, tournamentId);

      // Mettre à jour l'état
      setSuccess(`${importedAthletes.length} athlète(s) importé(s) avec succès.`);
      setStep('complete');

      // Appeler le callback de succès
      if (onImportSuccess) {
        onImportSuccess(importedAthletes);
      }
    } catch (err) {
      console.error('Erreur lors de l\'importation des athlètes:', err);
      setError(err.message || 'Erreur lors de l\'importation des athlètes. Veuillez réessayer.');
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  // Télécharger le modèle
  const handleDownloadTemplate = () => {
    console.log('SimpleImportAthletes - handleDownloadTemplate called');
    // Créer un classeur Excel
    const wb = XLSX.utils.book_new();
    
    // Données du modèle avec dateOfBirth au lieu de birthYear et 15 athlètes d'exemple
    const templateData = [
      ['firstName', 'lastName', 'gender', 'weight', 'dateOfBirth', 'club'],
      ['Jean', 'Dupont', 'Homme', 75, '1990-01-15', 'Club de Judo Paris'],
      ['Marie', 'Martin', 'Femme', 60, '1995-03-22', 'Club de Judo Lyon'],
      ['Thomas', 'Bernard', 'Homme', 82, '1988-07-10', 'Boxing Club Marseille'],
      ['Sophie', 'Petit', 'Femme', 58, '1992-11-05', 'Club de Boxe Nice'],
      ['Lucas', 'Robert', 'Homme', 78, '1985-09-18', 'Fight Club Toulouse'],
      ['Emma', 'Richard', 'Femme', 62, '1993-04-27', 'Boxing Academy Lille'],
      ['Hugo', 'Durand', 'Homme', 80, '1991-06-14', 'Club de Judo Bordeaux'],
      ['Léa', 'Moreau', 'Femme', 57, '1994-02-08', 'Fight Club Nantes'],
      ['Maxime', 'Simon', 'Homme', 85, '1987-12-03', 'Boxing Club Strasbourg'],
      ['Chloé', 'Laurent', 'Femme', 59, '1996-08-21', 'Club de Boxe Rennes'],
      ['Antoine', 'Michel', 'Homme', 76, '1989-05-17', 'Fight Academy Montpellier'],
      ['Julie', 'Lefebvre', 'Femme', 61, '1997-10-09', 'Club de Judo Reims'],
      ['Nicolas', 'Leroy', 'Homme', 83, '1986-03-25', 'Boxing Club Dijon'],
      ['Camille', 'Roux', 'Femme', 56, '1998-01-12', 'Fight Club Grenoble'],
      ['Alexandre', 'David', 'Homme', 88, '1984-11-30', 'Club de Boxe Toulon']
    ];
    
    console.log('Template data prepared:', templateData);
    
    try {
      // Créer une feuille de calcul
      const ws = XLSX.utils.aoa_to_sheet(templateData);
      console.log('Worksheet created');
      
      // Ajouter la feuille au classeur
      XLSX.utils.book_append_sheet(wb, ws, 'Template');
      console.log('Worksheet appended to workbook');
      
      // Télécharger le fichier
      XLSX.writeFile(wb, 'template_import_athletes.xlsx');
      console.log('Template file downloaded');
    } catch (err) {
      console.error('Erreur lors de la création du modèle:', err);
      console.error('Stack trace:', err.stack);
      alert('Erreur lors de la création du modèle: ' + err.message);
    }
  };

  // Réinitialiser le formulaire
  const handleReset = () => {
    setFile(null);
    setAthletes([]);
    setSelectedAthletes([]);
    setSelectAll(true);
    setError(null);
    setSuccess(null);
    setStep('upload');
  };

  return (
    <div className="simple-import-athletes">
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {step === 'upload' && (
        <>
          <p>
            Importez vos athlètes à partir d'un fichier Excel (.xlsx, .xls) ou CSV (.csv).
            Le fichier doit contenir les colonnes suivantes : firstName, lastName, gender, weight, dateOfBirth, club.
          </p>
          
          <Button 
            variant="outline-secondary" 
            className="mb-3"
            onClick={handleDownloadTemplate}
          >
            Télécharger le modèle
          </Button>
          
          <Form.Group controlId="formFile" className="mb-3">
            <Form.Label>Sélectionner un fichier</Form.Label>
            <Form.Control 
              type="file" 
              onChange={handleFileChange}
              accept=".xlsx,.xls,.csv"
            />
          </Form.Group>
          
          <div className="d-flex justify-content-end">
            <Button 
              variant="primary" 
              onClick={handleReadFile}
              disabled={!file || loading}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Lecture...
                </>
              ) : 'Continuer'}
            </Button>
          </div>
        </>
      )}

      {step === 'preview' && (
        <>
          <div className="mb-3">
            <h5>Aperçu des athlètes à importer</h5>
            <p>{athletes.length} athlète(s) trouvé(s) dans le fichier. Sélectionnez les athlètes que vous souhaitez importer.</p>
          </div>
          
          <div className="mb-3">
            <FormCheck 
              id="select-all-athletes"
              type="checkbox"
              label={`Sélectionner tous les athlètes (${athletes.length})`}
              checked={selectAll}
              onChange={handleSelectAll}
            />
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th style={{ width: '50px' }}>Sélection</th>
                <th>Prénom</th>
                <th>Nom</th>
                <th>Genre</th>
                <th>Poids (kg)</th>
                <th>Date de naissance</th>
                <th>Club</th>
              </tr>
            </thead>
            <tbody>
              {athletes.map((athlete, index) => (
                <tr key={index} className={selectedAthletes.includes(index) ? 'table-active' : ''}>
                  <td className="text-center">
                    <FormCheck 
                      type="checkbox"
                      checked={selectedAthletes.includes(index)}
                      onChange={() => handleSelectAthlete(index)}
                      aria-label={`Sélectionner ${athlete.firstName} ${athlete.lastName}`}
                    />
                  </td>
                  <td>{athlete.firstName}</td>
                  <td>{athlete.lastName}</td>
                  <td>{athlete.gender}</td>
                  <td>{athlete.weight}</td>
                  <td>{athlete.dateOfBirth || athlete.birthYear}</td>
                  <td>{athlete.club || '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          <div className="d-flex justify-content-between">
            <Button 
              variant="secondary" 
              onClick={handleReset}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button 
              variant="primary" 
              onClick={handleImportAthletes}
              disabled={loading || selectedAthletes.length === 0}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Importation...
                </>
              ) : `Importer ${selectedAthletes.length} athlète(s)`}
            </Button>
          </div>
        </>
      )}

      {step === 'importing' && (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Importation en cours...</span>
          </Spinner>
          <p className="mt-3">Importation des athlètes en cours...</p>
        </div>
      )}

      {step === 'complete' && (
        <>
          <Alert variant="success">
            {success}
          </Alert>
          <div className="d-flex justify-content-end">
            <Button 
              variant="primary" 
              onClick={handleReset}
            >
              Importer d'autres athlètes
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// Fonction pour lire un fichier Excel
const readExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        resolve(json);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = (error) => {
      reject(error);
    };
    
    reader.readAsArrayBuffer(file);
  });
};

// Fonction pour valider et transformer les données
const validateAndTransformData = (data) => {
  if (!data || data.length < 2) {
    return [];
  }
  
  // Récupérer les en-têtes
  const headers = data[0].map(header => String(header).trim().toLowerCase());
  
  // Vérifier les en-têtes requis
  const requiredHeaders = ['firstname', 'lastname', 'gender', 'weight'];
  const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
  
  if (missingHeaders.length > 0) {
    throw new Error(`En-têtes manquants: ${missingHeaders.join(', ')}`);
  }
  
  // Mapper les indices des colonnes
  const columnIndices = {
    firstName: headers.indexOf('firstname'),
    lastName: headers.indexOf('lastname'),
    gender: headers.indexOf('gender'),
    weight: headers.indexOf('weight'),
    dateOfBirth: headers.indexOf('dateofbirth'),
    birthYear: headers.indexOf('birthyear'),
    club: headers.indexOf('club')
  };
  
  console.log('Column indices:', columnIndices);
  
  // Transformer les données
  const athletes = [];
  
  for (let i = 1; i < data.length; i++) {
    const row = data[i];
    
    // Ignorer les lignes vides
    if (!row || row.length === 0) {
      continue;
    }
    
    // Vérifier les valeurs requises
    if (!row[columnIndices.firstName] || !row[columnIndices.lastName] || !row[columnIndices.weight]) {
      continue;
    }
    
    // Normaliser le genre
    let gender = String(row[columnIndices.gender] || '').trim();
    if (gender.toLowerCase() === 'homme' || gender.toLowerCase() === 'h' || gender.toLowerCase() === 'm' || gender.toLowerCase() === 'male') {
      gender = 'Homme';
    } else if (gender.toLowerCase() === 'femme' || gender.toLowerCase() === 'f' || gender.toLowerCase() === 'female') {
      gender = 'Femme';
    } else {
      gender = 'Autre';
    }
    
    // Créer l'athlète
    const athlete = {
      firstName: String(row[columnIndices.firstName]).trim(),
      lastName: String(row[columnIndices.lastName]).trim(),
      gender,
      weight: parseFloat(row[columnIndices.weight]) || 0,
      dateOfBirth: columnIndices.dateOfBirth >= 0 ? String(row[columnIndices.dateOfBirth] || '').trim() : null,
      birthYear: columnIndices.birthYear >= 0 ? (parseInt(row[columnIndices.birthYear]) || new Date().getFullYear() - 20) : new Date().getFullYear() - 20,
      club: columnIndices.club >= 0 ? String(row[columnIndices.club] || '').trim() : ''
    };
    
    // Si dateOfBirth n'est pas défini mais birthYear l'est, on crée une date par défaut (1er janvier)
    if (!athlete.dateOfBirth && athlete.birthYear) {
      athlete.dateOfBirth = `${athlete.birthYear}-01-01`;
    }
    
    athletes.push(athlete);
  }
  
  return athletes;
};

export default SimpleImportAthletes;
